import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Card, Empty, Select, Skeleton, Space, Tag, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { productApi } from '../../../api';

/**
 * Admin surface — Academic calendar runtime.
 * Reads the current session's terms + holidays + exam datesheet and renders
 * a month grid with day cells labelled. Every module reads dates from here (S-06).
 */
const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function CalendarRuntimePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [monthOffset, setMonthOffset] = useState(0);

  const cal = useQuery({
    queryKey: ['calendar', institution],
    queryFn: () => productApi.getCalendar(institution),
    enabled: !!institution,
  });
  const exams = useQuery({
    queryKey: ['exams', institution],
    queryFn: () => productApi.listExams(institution),
    enabled: !!institution,
  });

  const current = cal.data?.current_session ?? null;
  const anchor = useMemo(() => {
    const d = new Date();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + monthOffset);
    return d;
  }, [monthOffset]);
  const monthLabel = anchor.toLocaleString('en-GB', { month: 'long', year: 'numeric' });

  // Build the grid: first Monday-of-week that contains day 1 → last Sunday-of-week that contains last day.
  const cells = useMemo(() => buildMonthGrid(anchor), [anchor]);

  // Lookup maps for the whole session
  const holidayByDate = new Map((cal.data?.holidays ?? []).map((h) => [h.date, h.label]));
  const termByDate = (isoDate: string): { label: string } | null => {
    for (const t of cal.data?.terms ?? []) {
      if (isoDate >= t.starts_on && isoDate <= t.ends_on) return { label: t.label };
    }
    return null;
  };
  // Exams per day = union of subjects with that date across all exams' subjects
  // (subjects live on exam detail, not on list — skip for now to avoid N+1).

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Academic calendar</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Every date in the product reads from here. One calendar per institution (S-06).
          </Typography.Text>
        </div>
      </div>

      {cal.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
      {cal.data && !current && (
        <Alert
          type="warning"
          showIcon
          message="No current academic session"
          description={<span>Add one in <a href={`/i/${institution}/setup/calendar`}>Setup step 2</a>.</span>}
        />
      )}

      {current && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Tag color="success">Session {current.label}</Tag>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {current.starts_on} → {current.ends_on}
            </Typography.Text>
            <div style={{ flex: 1 }} />
            <Space size={4}>
              <a onClick={() => setMonthOffset((n) => n - 1)}><LeftOutlined /> Prev</a>
              <Select
                size="small"
                value={monthOffset}
                onChange={setMonthOffset}
                style={{ width: 200 }}
                options={rangeMonths(-12, 12).map((n) => ({
                  value: n,
                  label: offsetLabel(n),
                }))}
              />
              <a onClick={() => setMonthOffset((n) => n + 1)}>Next <RightOutlined /></a>
            </Space>
          </div>

          <Card size="small" title={monthLabel}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
              {DAY_HEADERS.map((d) => (
                <div key={d} style={dayHeadStyle}>{d}</div>
              ))}
              {cells.map((c, i) => {
                const iso = c.iso;
                const holiday = iso ? holidayByDate.get(iso) : undefined;
                const term = iso ? termByDate(iso) : null;
                const inSession = iso && current && iso >= current.starts_on && iso <= current.ends_on;
                const isSunday = c.dayOfWeek === 7;
                return (
                  <div
                    key={i}
                    style={{
                      ...dayCellStyle,
                      opacity: c.iso ? 1 : 0.15,
                      background: holiday ? '#fff3ea' : isSunday ? '#f7f4fd' : c.isToday ? '#eaf3fb' : '#fff',
                      borderColor: c.isToday ? '#2f6ab0' : '#eeeef1',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontWeight: c.isToday ? 700 : 500, fontSize: 13, color: c.isToday ? '#2f6ab0' : '#14213d' }}>
                        {c.day ?? ''}
                      </span>
                      {inSession && term && <Typography.Text type="secondary" style={{ fontSize: 9, letterSpacing: 0.3 }}>{term.label}</Typography.Text>}
                    </div>
                    {holiday && (
                      <div style={{ marginTop: 4, fontSize: 10.5, color: '#a35800', fontWeight: 600, lineHeight: 1.2 }}>
                        {holiday}
                      </div>
                    )}
                    {isSunday && !holiday && (
                      <div style={{ marginTop: 4, fontSize: 10, color: '#8a94a6' }}>Sunday</div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Card size="small" title={`Terms in ${current.label}`}>
              {(cal.data?.terms.filter((t) => t.session_id === current.id) ?? []).length === 0 ? (
                <Empty description="No terms defined" style={{ padding: 8 }} />
              ) : (
                (cal.data?.terms.filter((t) => t.session_id === current.id) ?? []).map((t) => (
                  <div key={t.id} style={{ padding: '6px 0', borderTop: '1px solid #f1f1f4', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <b>{t.label}</b>
                    <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12, color: '#5b6478' }}>
                      {t.starts_on} → {t.ends_on}
                    </span>
                  </div>
                ))
              )}
            </Card>
            <Card size="small" title={`Upcoming holidays`}>
              {(cal.data?.holidays.filter((h) => h.date >= new Date().toISOString().slice(0, 10)).slice(0, 8) ?? []).length === 0 ? (
                <Empty description="No upcoming holidays" style={{ padding: 8 }} />
              ) : (
                cal.data!.holidays.filter((h) => h.date >= new Date().toISOString().slice(0, 10)).slice(0, 8).map((h) => (
                  <div key={h.id} style={{ padding: '6px 0', borderTop: '1px solid #f1f1f4', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>{h.label}</span>
                    <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12, color: '#5b6478' }}>{h.date}</span>
                  </div>
                ))
              )}
            </Card>
          </div>

          {exams.data && exams.data.total > 0 && (
            <Card size="small" title={`Upcoming exams (${exams.data.total})`}>
              <Space wrap size={6}>
                {exams.data.rows.slice(0, 12).map((e) => (
                  <Tag key={e.id} color={e.status === 'published' ? 'success' : 'processing'}>
                    {e.name} · {e.exam_type.replace(/_/g, ' ')}
                  </Tag>
                ))}
              </Space>
            </Card>
          )}
        </>
      )}
    </Space>
  );
}

const dayHeadStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: 10.5,
  color: '#8a94a6',
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  textAlign: 'left',
};
const dayCellStyle: React.CSSProperties = {
  minHeight: 78,
  padding: 6,
  border: '1px solid #eeeef1',
  borderRadius: 4,
};

interface Cell { day: number | null; iso: string | null; dayOfWeek: number; isToday: boolean }

function buildMonthGrid(anchor: Date): Cell[] {
  const year = anchor.getUTCFullYear();
  const month = anchor.getUTCMonth();
  const firstOfMonth = new Date(Date.UTC(year, month, 1));
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const iso0 = new Date().toISOString().slice(0, 10);
  const cells: Cell[] = [];
  const firstDow = ((firstOfMonth.getUTCDay() + 6) % 7); // Monday=0..Sunday=6
  for (let i = 0; i < firstDow; i++) {
    cells.push({ day: null, iso: null, dayOfWeek: (i % 7) + 1, isToday: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dt = new Date(Date.UTC(year, month, d));
    const dow = ((dt.getUTCDay() + 6) % 7) + 1;
    cells.push({ day: d, iso, dayOfWeek: dow, isToday: iso === iso0 });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ day: null, iso: null, dayOfWeek: ((cells.length % 7) + 1), isToday: false });
  }
  return cells;
}

function rangeMonths(from: number, to: number): number[] {
  const arr: number[] = [];
  for (let i = from; i <= to; i++) arr.push(i);
  return arr;
}
function offsetLabel(offset: number): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + offset);
  const label = d.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
  return offset === 0 ? `${label} · today` : label;
}
